import React, { useEffect, useRef, useState } from 'react';
import { QueryClientProvider, useIsFetching } from '@tanstack/react-query';
import { Router } from 'react-router-dom';
import history from 'infrastructure/history';
import UploadSummaryV2 from 'components/v2/UploadSummary';
import SignalRContextProvider from 'infrastructure/signalR/SignalRContext';
import FeatureFlagsContextProvider from 'infrastructure/features/FeatureFlagsContext';
import addFontAwesomeIconsToLibrary from 'infrastructure/icons/FontAwesomeIcons';
import UserContextProvider from 'infrastructure/currentUser/UserContext';
import AxiosContextProvider from 'infrastructure/axios/AxiosContext';
import UploadsContextProvider, {
  useUploadsContext,
} from 'infrastructure/uploads/UploadsContext';
import { UploadQueueProvider } from 'infrastructure/uploads';
import { AppInsightsContextProvider } from 'infrastructure/telemetry/AppInsights';
import MenuCountersContextProvider from 'infrastructure/MenuContext/MenuCountersContext';
import ScrollRestoration from 'infrastructure/scrollRestoration/ScrollRestoration';
import BlankLoadingGlobalWrapper from 'components/GlobalAppWrapper/BlankLoadingGlobalWrapper';
import CurrentTenantUploadedFontsProvider from 'infrastructure/currentTenant/CurrentTenantUploadedFontsContext';
import { getAccessToken } from 'infrastructure/security/accessTokenManager';
import isPremiereBrowserPath from './routeClassification';
import AppRouter from '../../Router';
import queryClient from './queryClient';

addFontAwesomeIconsToLibrary();

const nonDashboardRoutes = [
  '/',
  '/login',
  '/forgot-password',
  '/reset-password',
  '/password-reset-confirmation',
  '/error',
  '/500',
  '/404',
  '/premiere/authorize',
  '/premiere/workspaces/:workspaceNavigationKey',
  '/premiere/workspaces/:workspaceNavigationKey/mediaLibraries/externalAssets/:assetId',
];

const cardPreviewPublicRoute = '/cards/:id/preview/public';

const previewRoutes = [
  '/stories/:id/preview/public',
  '/interstitialAd/:id/preview/public',
  '/clips/:id/preview/public',
  cardPreviewPublicRoute,
];
const uploadedFontPreviewRoutes = [cardPreviewPublicRoute];

const matchRoute = (pathname: string, routes: Array<string>) => {
  const routeRegex = routes.map(
    route => new RegExp(`^${route.replace(/:[^/]+/g, '[^/]+')}$`)
  );
  return routeRegex.some(regex => regex.test(pathname));
};

const isNonDashboardRoute = (pathname: string) =>
  isPremiereBrowserPath(pathname) || matchRoute(pathname, nonDashboardRoutes);

const UploadsV2 = () => {
  const { dismissFailedUpload, files } = useUploadsContext();
  return (
    <UploadSummaryV2
      files={files}
      onDismissFailedUpload={dismissFailedUpload}
    />
  );
};

const RouterWrapper: React.FC<{
  setIsInitialLoading: (value: boolean) => void;
}> = ({ setIsInitialLoading }) => {
  const isFetching = useIsFetching();
  const hasMounted = useRef(false);

  useEffect(() => {
    if (
      (!hasMounted.current && isFetching > 0) ||
      isNonDashboardRoute(history.location.pathname)
    ) {
      hasMounted.current = true;
    }

    if (hasMounted.current && isFetching === 0) {
      setIsInitialLoading(false);
    }
  }, [isFetching, setIsInitialLoading]);

  return (
    <Router history={history}>
      <ScrollRestoration>
        <>
          <AppRouter />
          <UploadsV2 />
        </>
      </ScrollRestoration>
    </Router>
  );
};

const App: React.FC = () => {
  const [pathname, setPathname] = useState(history.location.pathname);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  useEffect(() => {
    const unlisten = history.listen(update => {
      setPathname(update.location.pathname);
    });
    return () => unlisten();
  }, []);

  const isPreviewRoute = matchRoute(pathname, previewRoutes);
  const isUploadedFontPreviewRoute = matchRoute(
    pathname,
    uploadedFontPreviewRoutes
  );
  const isDashboardRoute = !isPreviewRoute && !isNonDashboardRoute(pathname);
  const hasAccessToken = !!getAccessToken();
  const shouldLoadUploadedFonts =
    isDashboardRoute || isUploadedFontPreviewRoute;
  const router = <RouterWrapper setIsInitialLoading={setIsInitialLoading} />;

  return (
    <>
      {isInitialLoading && (
        // This can't be wrapped inside other providers as it needs to be visible immediately
        <BlankLoadingGlobalWrapper isDashboardView={isDashboardRoute} />
      )}
      <AxiosContextProvider>
        <QueryClientProvider client={queryClient}>
          <UserContextProvider>
            <AppInsightsContextProvider>
              <FeatureFlagsContextProvider>
                <MenuCountersContextProvider>
                  <SignalRContextProvider>
                    <UploadsContextProvider>
                      <UploadQueueProvider>
                        {shouldLoadUploadedFonts ? (
                          <CurrentTenantUploadedFontsProvider
                            loadCmsUploadedFonts={
                              isDashboardRoute || hasAccessToken
                            }
                            skipAuthRedirect={isUploadedFontPreviewRoute}
                          >
                            {router}
                          </CurrentTenantUploadedFontsProvider>
                        ) : (
                          router
                        )}
                      </UploadQueueProvider>
                    </UploadsContextProvider>
                  </SignalRContextProvider>
                </MenuCountersContextProvider>
              </FeatureFlagsContextProvider>
            </AppInsightsContextProvider>
          </UserContextProvider>
        </QueryClientProvider>
      </AxiosContextProvider>
    </>
  );
};

export default App;
